/**
 * Brazilian CNPJ (corporate tax ID) — 14 digits with check digits.
 *
 * Display format: XX.XXX.XXX/XXXX-XX (eg. 12.345.678/0001-90)
 * The last two digits are mod-11 check digits computed from the first twelve.
 */

/**
 * Strip every non-digit. Result is 0–14 chars.
 *
 * cleanCnpj("12.345.678/0001-90") → "12345678000190"
 * cleanCnpj("abc")                → ""
 */
export function cleanCnpj(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validates the 14-digit CNPJ including both check digits via the canonical
 * mod-11 algorithm. Returns false for:
 *   - lengths other than 14
 *   - all-same-digit strings (11111111111111 etc — algorithmically valid but
 *     reserved as invalid by Receita Federal)
 *   - bad check digits
 *
 * isValidCnpj("12.345.678/0001-90") → false (random sample)
 * isValidCnpj("11.222.333/0001-81") → true
 * isValidCnpj("11111111111111")     → false
 * isValidCnpj("")                   → false
 */
export function isValidCnpj(cnpj: string): boolean {
  const digits = cleanCnpj(cnpj);
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;

  const nums = digits.split('').map(Number);

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += nums[i]! * weights1[i]!;
  }
  let remainder = sum % 11;
  const check1 = remainder < 2 ? 0 : 11 - remainder;
  if (nums[12] !== check1) return false;

  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += nums[i]! * weights2[i]!;
  }
  remainder = sum % 11;
  const check2 = remainder < 2 ? 0 : 11 - remainder;
  return nums[13] === check2;
}

/**
 * Display mask: XX.XXX.XXX/XXXX-XX. Inserts separators progressively as the
 * user types — partial strings keep partial formatting.
 *
 * maskCnpj("12345678000190") → "12.345.678/0001-90"
 * maskCnpj("12345")          → "12.345"
 * maskCnpj("12")             → "12"
 * maskCnpj("")               → ""
 */
export function maskCnpj(value: string): string {
  const digits = cleanCnpj(value).slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}
